import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Upload, Download, Trash2, CheckCircle2 } from "lucide-react";
import { readFileAsBase64, PreviewButton } from "@/components/FilePreview";

const MAX_BYTES = 10 * 1024 * 1024;
const GROUP_TITLES = { required: "Zorunlu Belgeler", optional: "Opsiyonel Belgeler", exit: "İşten Çıkış Belgeleri" };
const EXIT_HINTS = { exit_resignation: "İstifa durumunda", exit_termination: "İşveren çıkarması durumunda" };
const SHARED_HSE_LABELS = { vocational: "MYK Belgesi", health_report: "Sağlık Raporu" };

const fmtAt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export function MissingDocsIcon({ person }) {
  const missing = person?.missing_docs || [];
  if (!person?.active || !missing.length) return null;
  return (
    <span className="inline-flex items-center text-[#FF9500] ml-2 align-middle" title={`Belgeler Eksik:\n• ${missing.join("\n• ")}`}
      data-testid={`personnel-missing-docs-${person.id}`}>
      <AlertTriangle size={15} />
    </span>
  );
}

export function MissingDocsBanner({ person, onClick }) {
  const missing = person?.missing_docs || [];
  if (!person?.active || !missing.length) return null;
  return (
    <button type="button" onClick={onClick} data-testid="detail-missing-docs-banner"
      className="w-full text-left flex items-start gap-2 border-2 border-[#FF9500] bg-[#FF9500]/10 px-3 py-2 hover:bg-[#FF9500]/20 transition-colors">
      <AlertTriangle size={16} className="text-[#FF9500] mt-0.5 shrink-0" />
      <span className="text-xs">
        <span className="font-mono uppercase font-bold text-[#B36800]">Belgeler Eksik ({6 - missing.length}/6)</span>
        <span className="block text-neutral-700">{missing.join(", ")}</span>
      </span>
    </button>
  );
}

function SharedDates({ pid, doc, onChanged }) {
  const [issue, setIssue] = useState(doc.issue_date || "");
  const [expiry, setExpiry] = useState(doc.expiry_date || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setIssue(doc.issue_date || ""); setExpiry(doc.expiry_date || ""); }, [doc.issue_date, doc.expiry_date]);
  const dirty = issue !== (doc.issue_date || "") || expiry !== (doc.expiry_date || "");
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/personnel/employees/${pid}/files/${doc.doc_type}`, { issue_date: issue, expiry_date: expiry });
      toast.success("Tarihler kaydedildi");
      onChanged(data.missing);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const cls = "h-8 border-2 border-[#09090B] px-2 text-xs font-mono bg-white";
  return (
    <div className="w-full flex flex-wrap items-end gap-2 pl-5" data-testid={`personnel-file-dates-${doc.doc_type}`}>
      <label className="text-[10px] font-mono uppercase text-neutral-500">Düzenleme Tarihi
        <input type="date" value={issue} onChange={(e) => setIssue(e.target.value)} className={`${cls} block`} data-testid={`personnel-file-issue-${doc.doc_type}`} />
      </label>
      {doc.doc_type !== "health_report" && (
      <label className="text-[10px] font-mono uppercase text-neutral-500">Geçerlilik Tarihi
        <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className={`${cls} block`} data-testid={`personnel-file-expiry-${doc.doc_type}`} />
      </label>
      )}
      <Button variant="outline" disabled={!dirty || saving} onClick={save} className="rounded-none border-2 border-[#09090B] h-8 text-xs"
        data-testid={`personnel-file-dates-save-${doc.doc_type}`}>{saving ? "Kaydediliyor…" : "Tarihleri Kaydet"}</Button>
    </div>
  );
}

function FileRow({ pid, doc, onChanged }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const url = `/personnel/employees/${pid}/files/${doc.doc_type}/download`;

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) { toast.error("Dosya çok büyük (max 10 MB)"); return; }
    setBusy(true);
    try {
      const f = await readFileAsBase64(file);
      const { data } = await api.put(`/personnel/employees/${pid}/files/${doc.doc_type}`, { file_b64: f.b64, file_name: f.name, content_type: f.content_type });
      toast.success(`${doc.label} yüklendi`);
      onChanged(data.missing);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yükleme başarısız"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const remove = async () => {
    if (!window.confirm(`${doc.label} silinsin mi?`)) return;
    const { data } = await api.delete(`/personnel/employees/${pid}/files/${doc.doc_type}`);
    toast.success("Belge silindi");
    onChanged(data.missing);
  };
  const download = async () => {
    const res = await api.get(url, { responseType: "blob" });
    const u = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = u; a.download = doc.file_name || doc.label; a.click();
    URL.revokeObjectURL(u);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 py-2 border-b border-neutral-200 last:border-b-0" data-testid={`personnel-file-row-${doc.doc_type}`}>
      <div className="flex-1 min-w-[180px]">
        <div className="text-sm font-semibold flex items-center gap-1.5">
          {doc.has_file
            ? <CheckCircle2 size={14} className="text-[#00A650]" />
            : <AlertTriangle size={14} className={doc.group === "required" ? "text-[#FF9500]" : "text-neutral-400"} />}
          {doc.label}
        </div>
        {(doc.group === "optional" || doc.shared) && (
          <div className="flex flex-wrap gap-1 pl-5 mt-0.5">
            {doc.group === "optional" && <span className="text-[9px] font-mono uppercase text-neutral-500 border border-neutral-300 px-1">Opsiyonel</span>}
            {doc.shared && <span className="text-[9px] font-mono uppercase text-[#0055FF] border border-[#0055FF] px-1" title={`İSG modülündeki ${SHARED_HSE_LABELS[doc.doc_type]} ile aynı kayıt`} data-testid={`personnel-file-shared-${doc.doc_type}`}>İSG · {SHARED_HSE_LABELS[doc.doc_type]} ile ortak</span>}
          </div>
        )}
        {EXIT_HINTS[doc.doc_type] && <div className="text-[11px] text-neutral-500">{EXIT_HINTS[doc.doc_type]}</div>}
        <div className="text-[11px] font-mono text-neutral-500 truncate" data-testid={`personnel-file-meta-${doc.doc_type}`}>
          {doc.has_file ? `${doc.file_name} · Yüklenme: ${fmtAt(doc.uploaded_at)}` : "Yüklenmedi"}
        </div>
      </div>
      <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden"
        onChange={(e) => upload(e.target.files?.[0])} data-testid={`personnel-file-input-${doc.doc_type}`} />
      <Button variant="outline" disabled={busy} className="rounded-none border-2 border-[#09090B] h-8 text-xs"
        onClick={() => ref.current?.click()} data-testid={`personnel-file-upload-${doc.doc_type}`}>
        <Upload size={13} className="mr-1" /> {busy ? "Yükleniyor…" : doc.has_file ? "Değiştir" : "Yükle"}
      </Button>
      {doc.has_file && (
        <>
          <PreviewButton doc={doc} fileUrl={url} className="h-8" testid={`personnel-file-preview-${doc.doc_type}`}
            dialogTestid="personnel-file-preview-dialog" imgTestid="personnel-file-preview-img" />
          <Button variant="outline" className="rounded-none border-2 border-[#09090B] h-8" onClick={download} title="İndir"
            data-testid={`personnel-file-download-${doc.doc_type}`}><Download size={13} /></Button>
        </>
      )}
      {(doc.has_file || (doc.shared && doc.has_record)) && (
          <Button variant="outline" className="rounded-none border-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white h-8"
            onClick={remove} title="Sil" data-testid={`personnel-file-delete-${doc.doc_type}`}><Trash2 size={13} /></Button>
      )}
      {doc.shared && <SharedDates pid={pid} doc={doc} onChanged={onChanged} />}
    </div>
  );
}

function useFiles(pid) {
  const [items, setItems] = useState(null);
  const load = async () => {
    const { data } = await api.get(`/personnel/employees/${pid}/files?_ts=${Date.now()}`);
    setItems(data.items);
    return data;
  };
  useEffect(() => { if (pid) load().catch(() => setItems([])); }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  return { items, load };
}

export function PersonnelFilesSection({ pid, onMissingChange }) {
  const { items, load } = useFiles(pid);
  const changed = async (missing) => { await load(); onMissingChange?.(missing); };
  if (!items) return <div className="text-sm text-neutral-500 py-4">Yükleniyor…</div>;
  return (
    <div className="space-y-4" data-testid="personnel-files-section">
      {Object.entries(GROUP_TITLES).map(([g, title]) => (
        <div key={g}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">{title}</div>
          <div className="border-t-2 border-[#09090B]">
            {items.filter((d) => d.group === g).map((d) => <FileRow key={d.doc_type} pid={pid} doc={d} onChanged={changed} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

export function DeactivateDialog({ person, onClose, onDone }) {
  const { items, load } = useFiles(person?.id);
  const [saving, setSaving] = useState(false);
  const exitDocs = (items || []).filter((d) => d.group === "exit");
  const ready = exitDocs.some((d) => d.has_file);

  const confirm = async () => {
    if (!ready) { toast.warning("Pasife almak için İşten Ayrılış Bildirgesi veya Fesih Belgesi yükleyin"); return; }
    setSaving(true);
    try {
      await api.patch(`/personnel/employees/${person.id}/active`, { active: false });
      toast.success("Pasif yapıldı");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!person} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-none border-2 border-[#09090B] bg-white max-w-xl" data-testid="personnel-deactivate-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tight">{person?.name} · Pasife Al</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-neutral-600">Duruma göre aşağıdaki belgelerden en az birini yükleyin.</p>
        <div className="border-t-2 border-[#09090B]">
          {items === null ? <div className="text-sm text-neutral-500 py-4">Yükleniyor…</div>
            : exitDocs.map((d) => <FileRow key={d.doc_type} pid={person.id} doc={d} onChanged={load} />)}
        </div>
        {items && !ready && (
          <div className="flex items-center gap-2 text-xs text-[#B36800] border-2 border-[#FF9500] bg-[#FF9500]/10 px-3 py-2" data-testid="deactivate-warning">
            <AlertTriangle size={14} /> İki belge de boş. Pasife alma için en az biri yüklenmelidir.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={onClose} data-testid="deactivate-cancel">Vazgeç</Button>
          <Button className="rounded-none bg-[#09090B] hover:bg-[#FF3B30]" disabled={saving} onClick={confirm} data-testid="deactivate-confirm">
            {saving ? "Kaydediliyor…" : "Pasife Al"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
